import { CSSResultGroup, html, TemplateResult } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property, query } from 'lit/decorators.js';
import { iconList, IconList } from './iconList';
import { iconLibraries } from './iconLibraries';
import { styleMap } from 'lit/directives/style-map.js';
import componentStyles from './ghs-icon.css.lit';
import { MobileController } from '../../controllers/mobile-controller';

export type LibraryType =
  | 'regular'
  | 'solid'
  | 'bellinGeneral'
  | 'bellinSocial';

/**
 * The OutlineIcon component
 * @element ghs-icon
 */
@customElement('ghs-icon')
export class GhsIcon extends OutlineElement {
  private mobileController = new MobileController(this);

  static styles: CSSResultGroup = [componentStyles];

  @query('svg')
  icon: SVGElement | undefined;

  /**
   * Color of icon, by default it inherits current text color from wrapper
   * @param color
   * @default currentColor
   */
  @property()
  color = 'currentColor';

  /**
   * Determines if svg is hidden from screen readers or not
   * @param decorative
   * @default false;
   */
  @property({ type: Boolean })
  decorative = false;

  /**
   * Listens for parent element size
   * @param sizeGrow
   * @default false;
   */
  @property({ type: Boolean })
  sizeGrow = false;

  /**
   * Name of icon to display
   * @param name
   */
  @property()
  name: keyof IconList | string;

  /**
   * The width and height of the icon on mobile screens
   * @param mobileSize in pixels
   * @default 16px
   */
  @property({ attribute: 'mobile-size' })
  mobileSize = '16px';

  /**
   * The library type of the icon
   * @param library in pixels
   */
  @property({ attribute: 'library' })
  library: LibraryType | undefined;

  /**
   * The width and height of the icon on screens larger than mobile
   * @param size in pixels
   * @default 32px
   */
  @property()
  size = '32px';

  firstUpdated() {
    super.firstUpdated();
    if (this.icon === null || this.icon === undefined) {
      return;
    }

    this.icon.setAttribute('aria-hidden', `${this.decorative}`);
  }

  render(): TemplateResult {
    const styles = {
      // eslint-disable-next-line no-nested-ternary
      width: this.sizeGrow
        ? '100%'
        : this.mobileController.isMobile
          ? this.mobileSize
          : this.size,
      // eslint-disable-next-line no-nested-ternary
      height: this.sizeGrow
        ? '100%'
        : this.mobileController.isMobile
          ? this.mobileSize
          : this.size,

      fill: this.color,
      color: this.color,
      display: 'flex',
    };

    if (this.library) {
      const library: IconList = iconLibraries[this.library];

      return html` <div style=${styleMap(styles)}>${library[this.name]}</div> `;
    }

    return html`<div style=${styleMap(styles)}>${iconList[this.name]}</div>`;
  }
}
